/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数。
 */
import { timestampDate } from '@bufbuild/protobuf/wkt'
import {
  SplitSupplyNoticeSection,
  type SplitSupplyPartnerNoticeListItem,
} from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useNavigate } from 'react-router'

import { TertiaryButton, Text, VStack } from '@enechain/ecloud-designsystem'

import { HintTooltip } from '~/components/common/HintTooltip'
import {
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { useMemberDisplayName } from '~/hooks/member/useMemberDisplayName'
import { openOrNavigate } from '~/libs/openOrNavigate'
import { convertAreaLabel } from '~/models/area'
import { protoDateToCivilDate } from '~/models/civilDate'
import { formatDateDisplay, formatJstDateTimeHm } from '~/models/dateTime'
import {
  PARTNER_NOTICE_CHANNEL_KEYS,
  partnerNoticeDetailSearch,
} from '~/pages/SplitSupplyPlan/models/partnerNotice'
import { routePaths } from '~/routes/routePaths'

type Props = {
  rows: SplitSupplyPartnerNoticeListItem[]
  isLoading: boolean
  /** 一覧取得エラー時のメッセージ。 */
  errorLabel?: string
}

const NO_VALUE = '—'

const detailPath = (item: SplitSupplyPartnerNoticeListItem): string => {
  const target = protoDateToCivilDate(item.targetDate)
  return target === undefined
    ? routePaths.planSplitSupply.path
    : `${routePaths.planSplitSupplyDetail.path}?${partnerNoticeDetailSearch(item.id, target)}`
}

/** 登録者 (eauth member) の表示名。 解決できない時は出さない (生の ID は出さない)。 */
const RegisteredByCell = ({
  memberId,
}: {
  memberId: string | undefined
}): React.ReactElement => {
  const resolveDisplayName = useMemberDisplayName()
  return <span>{resolveDisplayName(memberId) ?? NO_VALUE}</span>
}

/**
 * 他社の通告値の一覧。 1 行 = 登録 1 件 (断面 × 対象日 × エリア × 他社 で 1 件だけ、 履歴は持たない)。
 * 登録日時の新しい順 (backend が返す順)。 行クリックで詳細。
 */
export const PartnerNoticeTable = ({
  rows,
  isLoading,
  errorLabel,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const columns: Array<
    MasterDataTableColumn<SplitSupplyPartnerNoticeListItem>
  > = [
    {
      key: 'period',
      header: t('page:splitSupplyPlan.column.period'),
      nowrap: true,
      cell: (row) => {
        const target = protoDateToCivilDate(row.targetDate)
        if (target === undefined) return NO_VALUE
        return row.section === SplitSupplyNoticeSection.MONTHLY
          ? t('page:splitSupplyPlan.monthLabel', {
              year: target.year,
              month: target.month,
            })
          : formatDateDisplay(
              new Date(target.year, target.month - 1, target.day),
            )
      },
    },
    {
      key: 'area',
      header: t('page:splitSupplyPlan.column.area'),
      cell: (row) => convertAreaLabel(row.areaCode),
    },
    {
      key: 'counterparty',
      header: (
        <HintTooltip
          content={t('page:splitSupplyPlan.partner.columnHint.counterparty')}
        >
          <span>{t('page:splitSupplyPlan.partner.column.counterparty')}</span>
        </HintTooltip>
      ),
      cell: (row) => (
        <VStack alignItems="flex-start" gap={0}>
          <Text as="span">{row.counterpartyBusinessCode}</Text>
          {row.counterpartyBusinessName !== '' && (
            <Text as="span" textStyle="caption" color="Text.lowEmphasis">
              {row.counterpartyBusinessName}
            </Text>
          )}
        </VStack>
      ),
    },
    {
      key: 'points',
      header: t('page:splitSupplyPlan.column.points'),
      align: 'right',
      format: 'number',
      cell: (row) => (
        <HintTooltip
          content={row.points
            .map((p) =>
              p.customerName === ''
                ? p.supplyPointSpecificNumber
                : `${p.customerName} (${p.supplyPointSpecificNumber})`,
            )
            .join('\n')}
        >
          <span>
            {t('page:splitSupplyPlan.pointCount', {
              count: row.points.length,
            })}
          </span>
        </HintTooltip>
      ),
    },
    {
      key: 'registeredAt',
      header: t('page:splitSupplyPlan.partner.column.registeredAt'),
      nowrap: true,
      cell: (row) =>
        row.createdAt === undefined
          ? NO_VALUE
          : formatJstDateTimeHm(timestampDate(row.createdAt)),
    },
    {
      key: 'registeredBy',
      header: t('page:splitSupplyPlan.partner.column.registeredBy'),
      nowrap: true,
      cell: (row) => <RegisteredByCell memberId={row.createdBy} />,
    },
    {
      key: 'channel',
      header: t('page:splitSupplyPlan.partner.column.channel'),
      nowrap: true,
      cell: (row) => {
        const key = PARTNER_NOTICE_CHANNEL_KEYS[row.channel]
        if (key === undefined) return NO_VALUE
        return (
          <HintTooltip content={row.sourceFileName}>
            <span>{t(key)}</span>
          </HintTooltip>
        )
      },
    },
  ]

  return (
    <MasterDataTable
      actionColWidth="120px"
      columns={columns}
      emptyLabel={t('page:splitSupplyPlan.partner.empty')}
      errorLabel={errorLabel}
      getRowKey={(row) => row.id}
      isLoading={isLoading}
      onRowClick={(row, e) => {
        openOrNavigate(navigate, detailPath(row), e)
      }}
      renderRowActions={(row) => (
        <TertiaryButton
          // 行アクションの文字は md (14px) 維持 (SplitSupplyNoticeTable の「詳細」と同じ判断)
          fontSize="md"
          asChild
          paddingX={3}
        >
          <RouterLink to={detailPath(row)}>
            {t('page:splitSupplyPlan.actionDetail')}
          </RouterLink>
        </TertiaryButton>
      )}
      rows={rows}
    />
  )
}
